import React, { useEffect, useState } from "react";
import axios from "axios";
import AdminSidebar from "../../components/admin/AdminSidebar";
import { User, Shield, Mail, Phone, Trash2, UserCheck, BadgeCheck } from "lucide-react";
import { useModal } from "../../context/ModalContext";

const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";

export default function AdminUsers() {
  const { showAlert, showConfirm } = useModal();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${apiUrl}/api/admin/users`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token") || sessionStorage.getItem("token")}`,
        },
      });
      setUsers(res.data);
    } catch (err) {
      console.error("Failed to fetch users:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const toggleRole = async (id, currentRole) => {
    const newRole = currentRole === "admin" ? "user" : "admin";
    try {
      await axios.put(`${apiUrl}/api/admin/users/${id}/role`, { role: newRole }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token") || sessionStorage.getItem("token")}`,
        },
      });
      fetchUsers();
    } catch (err) {
      showAlert("Failed to update role", "error");
    }
  };

  const toggleVerification = async (id, currentStatus) => {
    try {
      await axios.put(`${apiUrl}/api/admin/users/${id}/verify`, { verified: !currentStatus }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token") || sessionStorage.getItem("token")}`,
        },
      });
      fetchUsers();
    } catch (err) {
      showAlert("Failed to update verification", "error");
    }
  };

  const handleDelete = async (id) => {
    showConfirm("Delete this user? This cannot be undone.", async () => {
      try {
        await axios.delete(`${apiUrl}/api/admin/users/${id}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token") || sessionStorage.getItem("token")}`,
          },
        });
        fetchUsers();
      } catch (err) {
        showAlert("Failed to delete user", "error");
      }
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <AdminSidebar />
      <main className="ml-64 flex-grow p-8">
        <header className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
          <p className="text-gray-500">Manage user accounts and permissions.</p>
        </header>

        {loading ? (
          <div className="text-gray-500">Loading users...</div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-gray-50 border-b border-gray-100 text-gray-600 text-sm uppercase tracking-wider">
                  <tr>
                    <th className="px-6 py-4 font-semibold">User</th>
                    <th className="px-6 py-4 font-semibold">Contact</th>
                    <th className="px-6 py-4 font-semibold">Role</th>
                    <th className="px-6 py-4 font-semibold">Email</th>
                    <th className="px-6 py-4 font-semibold">Seller Badge</th>
                    <th className="px-6 py-4 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {users.map((u) => (
                    <tr key={u.ID} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center text-green-600 relative">
                            <User size={20} />
                            {u.Verified === 1 && (
                              <div className="absolute -top-1 -right-1 bg-white rounded-full">
                                <BadgeCheck size={16} className="text-blue-500 fill-blue-50" />
                              </div>
                            )}
                          </div>
                          <div>
                            <p className="font-bold text-gray-900">{u.FirstName} {u.LastName}</p>
                            <p className="text-xs text-gray-500">ID: {u.ID}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2 text-xs text-gray-600">
                            <Mail size={12} /> {u.Email}
                          </div>
                          {u.Phone && (
                            <div className="flex items-center gap-2 text-xs text-gray-600">
                              <Phone size={12} /> {u.Phone}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1 w-fit ${
                          u.Role === 'admin' 
                            ? "bg-purple-100 text-purple-700" 
                            : "bg-gray-100 text-gray-700"
                        }`}>
                          {u.Role === 'admin' && <Shield size={12} />}
                          {u.Role}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase ${
                          u.isVerified ? "bg-green-50 text-green-600" : "bg-red-50 text-red-600"
                        }`}>
                          {u.isVerified ? "Verified" : "Unverified"}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase ${
                            u.Verified === 1 ? "bg-blue-50 text-blue-600" : "bg-gray-50 text-gray-400"
                          }`}>
                            {u.Verified === 1 ? "Official Seller" : "Normal"}
                          </span>
                          {u.Verified === 1 && <BadgeCheck size={14} className="text-blue-500" />}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <button 
                            onClick={() => toggleVerification(u.ID, u.Verified === 1)}
                            className={`p-2 rounded-lg transition-colors ${
                              u.Verified === 1 
                                ? "bg-gray-100 text-gray-400 hover:bg-gray-200" 
                                : "bg-blue-50 text-blue-600 hover:bg-blue-100"
                            }`}
                            title={u.Verified === 1 ? "Revoke Seller Badge" : "Verify as Seller"}
                          >
                            <BadgeCheck size={18} />
                          </button>
                          <button 
                            onClick={() => toggleRole(u.ID, u.Role)}
                            className={`p-2 rounded-lg transition-colors ${
                              u.Role === 'admin' 
                                ? "bg-amber-50 text-amber-600 hover:bg-amber-100" 
                                : "bg-purple-50 text-purple-600 hover:bg-purple-100"
                            }`}
                            title={u.Role === 'admin' ? "Revoke Admin" : "Make Admin"}
                          >
                            <UserCheck size={18} />
                          </button>
                          <button 
                            onClick={() => handleDelete(u.ID)}
                            className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                            title="Delete"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
